import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import { UserSelectors } from '@recruitee/account/data';
import { filterBoolean, Helpers } from '@recruitee/common';
import { Placement } from '@recruitee/http/types';
import { I18nService } from '@recruitee/i18n';
import { referencesToActionsFactory } from '@recruitee/ngrx';
import { JobsBriefActions, StageActions } from '@recruitee/offers/data';
import { DialogService, DialogWidth } from '@recruitee/user-interface';

import {
  PlacementDisqualifyParams,
  PlacementHttpClient,
  PlacementHttpReference,
} from './placement-http-client';
import { PlacementActions } from './placement.store';

@Injectable({ providedIn: 'root' })
export class PlacementEffects {
  public getReferencesActions = referencesToActionsFactory<PlacementHttpReference>()([
    { type: 'Offer', action: JobsBriefActions.references },
    { type: 'Stage', action: StageActions.references },
  ]);

  public create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.createOnePessimistic),
      mergeMap(({ payload: { candidateId, offerId, correlationId } }) =>
        this.placementHttpClient.create({ candidateId, offerId }).pipe(
          switchMap(({ references, placement }) => [
            ...this.getReferencesActions(references),
            PlacementActions.createOnePessimisticSuccess({
              payload: { data: placement, correlationId },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.createOnePessimisticFailed({ payload: { error, correlationId } })),
          ),
        ),
      ),
    ),
  );

  public delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.removeOnePessimistic),
      mergeMap(({ payload }) =>
        this.placementHttpClient.delete({ id: <number>payload.data }).pipe(
          switchMap(({ references, placement }) => [
            ...this.getReferencesActions(references),
            PlacementActions.removeOnePessimisticSuccess({ payload: { data: placement } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              PlacementActions.removeOnePessimisticFailed({
                payload: { error, data: payload.data },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public updatePositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.updatePositions),
      mergeMap(({ payload: { positions } }) =>
        this.placementHttpClient.updatePositions(positions).pipe(
          switchMap(({ references, placements }) => [
            ...this.getReferencesActions(references),
            PlacementActions.updatePositionsSuccess({ payload: { placements } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.updatePositionsFailed({ payload: { error, positions } })),
          ),
        ),
      ),
    ),
  );

  public updateCandidatePositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.updateCandidatePositions),
      mergeMap(({ payload: { positions } }) =>
        this.placementHttpClient.updateCandidatePositions(positions).pipe(
          switchMap(({ references, placements }) => [
            ...this.getReferencesActions(references),
            PlacementActions.updateCandidatePositionsSuccess({ payload: { placements } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.updateCandidatePositionsFailed({ payload: { error, positions } })),
          ),
        ),
      ),
    ),
  );

  public requalify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.requalify),
      mergeMap(({ payload }) =>
        this.placementHttpClient.requalify(payload.id).pipe(
          switchMap(({ references, placement }) => [
            ...this.getReferencesActions(references),
            PlacementActions.requalifySuccess({ payload: { placement } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.requalifyFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public disqualify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.disqualify),
      filterBoolean,
      mergeMap(({ payload: { params } }: { payload: { params: PlacementDisqualifyParams } }) =>
        this.placementHttpClient.disqualify(params).pipe(
          switchMap(({ references, placement }) => [
            ...this.getReferencesActions(references),
            PlacementActions.disqualifySuccess({ payload: { placement } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.disqualifyFailed({ payload: { error, params } })),
          ),
        ),
      ),
    ),
  );

  public changeStage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.changeStage),
      mergeMap(({ payload: { params, changedHireStatus } }) =>
        this.placementHttpClient.changeStage(params).pipe(
          switchMap(({ references, placement }) => [
            ...this.getReferencesActions(references),
            PlacementActions.changeStageSuccess({ payload: { placement, changedHireStatus } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.changeStageFailed({ payload: { error, params } })),
          ),
        ),
      ),
    ),
  );

  public fetchHiringDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.fetchHiringDetails),
      mergeMap(({ payload: { placementId } }) =>
        this.placementHttpClient.fetchHiringDetails(placementId).pipe(
          concatLatestFrom(({ placement }) =>
            this.store.select(UserSelectors.byId(placement.hiredById ?? '')),
          ),
          switchMap(([{ placement, references }, user]) => {
            const placementWithReferences: Placement = Helpers.fillInReferences(
              {
                ...placement,
                hiredBy: user,
              },
              references,
            );
            return [
              ...this.getReferencesActions(references),
              PlacementActions.fetchHiringDetailsSuccess({
                payload: { placement: placementWithReferences },
              }),
            ];
          }),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.fetchHiringDetailsFailed({ payload: { error, placementId } })),
          ),
        ),
      ),
    ),
  );

  public updateHiringDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlacementActions.updateHiringDetails),
      mergeMap(({ payload: { params } }) =>
        this.placementHttpClient.updateHiringDetails(params).pipe(
          concatLatestFrom(({ placement }) =>
            this.store.select(UserSelectors.byId(placement.hiredById ?? '')),
          ),
          switchMap(([{ placement, references }, user]) => {
            const placementWithReferences: Placement = Helpers.fillInReferences(
              {
                ...placement,
                hiredBy: user,
              },
              references,
            );
            return [
              ...this.getReferencesActions(references),
              PlacementActions.updateHiringDetailsSuccess({
                payload: { placement: placementWithReferences },
              }),
            ];
          }),
          catchError((error: HttpErrorResponse) =>
            of(PlacementActions.updateHiringDetailsFailed({ payload: { error, params } })),
          ),
        ),
      ),
    ),
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private placementHttpClient: PlacementHttpClient,
    private i18n: I18nService,
    private dialogService: DialogService,
  ) {}

  private showDisqualifyDialog(): Observable<boolean> {
    return this.dialogService.warning({
      width: DialogWidth.SmallXX,
      title: this.i18n.translate('app.candidate.disqualify_dialog.title'),
      message: this.i18n.translate('app.candidate.disqualify_dialog.message_html', { count: 1 }),
      agreeLabel: this.i18n.translate('app.candidate.disqualify_dialog.agree_label'),
    });
  }
}
